import useAuthState from '@/states/auth.state';
import useProcessesState from '@/states/processes.state';
import useTenantState from '@/states/tenant.state';
import { axiosInstance } from '@/utils/lib/axios';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'alurkerja-ui';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type ProtectedRouteProps = {
  layout: React.FC;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ layout: Layout }) => {
  const navigate = useNavigate();
  const { tenant_id } = useParams();
  const { loadTenants, tenants, loadUserProfile, userProfile, isLogoutLoading } = useAuthState();
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const { setCurrentTenant, currentTenant } = useTenantState();
  const { loadBusinessProcesses } = useProcessesState();
  const queryClient = useQueryClient();
  
  // Fetch token async on mount
  useEffect(() => {
    const fetchToken = async () => {
      const authToken = await useAuthState.getState().getToken();
      setToken(authToken);
      setIsInitializing(false);
    };
    fetchToken();
  }, []);

  useEffect(() => {
    const initializeTenants = async () => {
      if (token && !tenants) {
        try {
          await loadTenants();
        } catch (error) {
          console.error('Failed to load tenants:', error);
        }
      }
    };

    const initializeUserProfile = async () => {
      if (token) {
        try {
          await loadUserProfile(token, true);
        } catch (error) {
          console.error('Failed to load user profile:', error);
        }
      }
    };

    initializeTenants();
    initializeUserProfile();
  }, [token, loadTenants, tenants, loadUserProfile]);

  useEffect(() => {
    // Wait until token init is complete before validating
    if (isInitializing) return;

    // Validasi token
    if (!token) {
      console.error('No token found, redirecting to login');
      navigate(`/login?tenantId=${tenant_id}`);
      return;
    }

    axiosInstance.interceptors.request.use(
      config => {
        // Ambil token dan tenant terbaru langsung dari state setiap kali ada request
        const activeTenant = useTenantState.getState().currentTenant;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (activeTenant) {
          config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
        }

        config.headers['Content-Type'] = 'application/json';

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    // Validasi tenant_id dari URL
    if (!tenant_id || ['undefined', 'null', ''].includes(tenant_id)) {
      console.error('Tenant ID is not defined in URL');
      navigate(`/workspaces`);
      return;
    }

    if (!tenants?.filter(t => t.slug === tenant_id).length) {
      console.error(`Tenant with ID "${tenant_id}" not found`);
      setCurrentTenant(null);
      navigate(`/workspaces`);
      return;
    }

    // Tunggu tenants dimuat sebelum melakukan sync
    if (!tenants) {
      console.info('Tenants not loaded yet, waiting...');
      return;
    }

    // Jika tenant_id di URL berbeda dengan state, update state-nya
    if (currentTenant?.slug !== tenant_id) {
      console.info(
        `Syncing state: URL tenant is "${tenant_id}", state tenant was "${currentTenant?.slug}". Updating...`,
      );
      const tenant = tenants?.find(t => t.slug === tenant_id);
      setCurrentTenant(tenant!);
      queryClient.invalidateQueries();
    }

    if (token && currentTenant?.slug) {
      loadBusinessProcesses();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id, token, currentTenant, tenants, setCurrentTenant, isInitializing]); // Tambahkan tenants ke dependency

  if (!token) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        Loading...
      </div>
    );
  }


  if (isLogoutLoading ) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        {isLogoutLoading ? 'Logout...' : 'Loading...'}
      </div>
    );
  }

  if (userProfile?.tenant_role === 'member' || userProfile?.tenant_role === '' || userProfile?.tenant_role === undefined) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-10 rounded-lg shadow-lg text-center">
          <h1 className="text-6xl font-bold text-red-500 mb-4">403</h1>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Forbidden Access</h2>
          <p className="text-gray-600 mb-4">Current Role: <span className="font-bold">{userProfile?.tenant_role || 'No Role'}</span></p>
          <div className="mt-6">
            <button
              onClick={() => window.location.href = '/workspaces'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Kembali ke Workspaces
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <Layout />;
};
